import NotificationService from '@/services/NotificationService';
import ArquivoFilaProcessamentoService from '@/services/ArquivoFilaProcessamentoService';

/**
 * Handler específico para notificações de relatórios
 * Converte notificações de relatórios WebSocket para o formato genérico do sistema
 */
class ReportNotificationHandler {
  /**
   * Mapeia tipos de arquivos para labels amigáveis
   */
  static getTipoArquivoLabel(tipo) {
    const labels = {
      'PLANILHA_OPERACAO_DASHBOARD': 'Relatório do Dashboard',
      'PLANILHA_OPERACAO_ALERTAS_INIBIDOS': 'Relatório de Alertas Inibidos',
      'PLANILHA_OPERACAO_ALERTAS_NAO_INIBIDOS': 'Relatório de Alertas Não Inibidos',
      'PLANILHA_MONITORAMENTO_SOCIOAMBIENTAL': 'Relatório Socioambiental',
      'PLANILHA_MONITORAMENTO_SOCIOAMBIENTAL_DETALHADA': 'Relatório Socioambiental Detalhado'
    };
    return labels[tipo] || 'Relatório';
  }

  /**
   * Gera título baseado no status e tipo
   */
  static getNotificationTitle(status, tipoArquivo) {
    const tipoLabel = this.getTipoArquivoLabel(tipoArquivo);

    switch (status) {
      case 'PROCESSADO':
        return `${tipoLabel} Concluído!`;

      case 'EM_PROCESSAMENTO':
        return `Processando ${tipoLabel}...`;

      case 'ERRO':
        return `Erro ao Processar ${tipoLabel}`;

      case 'SOLICITADO':
        return `${tipoLabel} Solicitado`;

      default:
        return tipoLabel;
    }
  }

  /**
   * Cria ação de download para relatório
   * @param {string} notificationId - ID da notificação (usado para remover após download)
   * @param {string} nomeArquivo - Nome do arquivo a ser baixado
   */
  static createDownloadAction(notificationId, nomeArquivo) {
    return {
      label: 'Baixar',
      icon: 'el-icon-download',
      type: 'primary',
      handler: async (vm, notification) => {
        try {
          // Usar nome do arquivo para download
          await ArquivoFilaProcessamentoService.downloadArquivo(nomeArquivo);

          if (vm && vm.$message) {
            vm.$message.success('Download iniciado!');
          }

          // Remover notificação após download
          setTimeout(() => {
            NotificationService.removeNotification(notificationId);
          }, 2000);

        } catch (error) {
          if (vm && vm.$message) {
            vm.$message.error('Erro ao fazer download do arquivo');
          }
        }
      }
    };
  }

  /**
   * Converte notificação de relatório WebSocket para formato genérico
   */
  static handleReportNotification(reportNotification) {
    // Verificar se é o novo formato (GenericNotificationDTO) ou formato antigo
    const isNewFormat = reportNotification.metadata !== undefined;

    // Normalizar para formato interno
    let normalizedNotification;
    if (isNewFormat) {
      // Novo formato do backend (GenericNotificationDTO)
      normalizedNotification = {
        arquivoId: reportNotification.metadata?.arquivoId || reportNotification.id,
        status: reportNotification.status,
        tipoArquivo: reportNotification.metadata?.tipoArquivo,
        mensagem: reportNotification.message,
        nomeArquivo: reportNotification.metadata?.nomeArquivo,
        timestamp: reportNotification.timestamp
      };
    } else {
      // Formato antigo (ReportNotificationDTO)
      normalizedNotification = reportNotification;
    }

    // Ignorar notificações de SOLICITADO (tratadas em FileGenerationUtils)
    if (normalizedNotification.status === 'SOLICITADO') {
      return null;
    }

    // Gerar ID único para a notificação
    // Usa arquivoId para que atualizações do mesmo arquivo substituam a notificação anterior
    let notificationId;
    if (normalizedNotification.arquivoId) {
      notificationId = `report_${normalizedNotification.arquivoId}`;
    } else {
      // Fallback: usa timestamp + random para garantir unicidade
      notificationId = `report_temp_${normalizedNotification.timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // Criar notificação para Toast + SidePanel
    const notification = {
      id: notificationId,
      type: 'REPORT',
      status: normalizedNotification.status,
      title: isNewFormat ? reportNotification.title : this.getNotificationTitle(normalizedNotification.status, normalizedNotification.tipoArquivo),
      message: normalizedNotification.mensagem,
      category: 'Relatórios',
      timestamp: normalizedNotification.timestamp,
      metadata: {
        tipoArquivo: normalizedNotification.tipoArquivo,
        nomeArquivo: normalizedNotification.nomeArquivo,
        arquivoId: normalizedNotification.arquivoId
      },
      actions: []
    };

    // Adicionar ação de download se processado
    if (normalizedNotification.status === 'PROCESSADO' && normalizedNotification.nomeArquivo) {
      notification.actions.push(
        this.createDownloadAction(notificationId, normalizedNotification.nomeArquivo)
      );
    }

    // Adicionar ao sistema de notificações (Toast + SidePanel)
    NotificationService.addNotification(notification);

    return notification;
  }

  /**
   * Inicializa listener para notificações de relatórios via WebSocket
   */
  static initializeReportListener() {
    window.addEventListener('report-notification', (event) => {
      const reportNotification = event.detail;
      this.handleReportNotification(reportNotification);
    });
  }
}

export default ReportNotificationHandler;
